import fetchProduct from "@api/operations/fetch-product";
import Product from "@api/types/product";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { NextSeo, ProductJsonLd } from "next-seo";
import { useRouter } from "next/router";
import React, { useContext } from "react";

import Container from "@components/Container";
import EnrollButton from "@components/EnrollButton";
import LinkButton from "@components/LinkButton";
import MainLayout from "@components/MainLayout";
import ProductPageSkeleton from "@components/ProductPageSkeleton";
import Spacer from "@components/Spacer";
import UserContext from "@components/UserContext";

export const getServerSideProps: GetServerSideProps = async ({ req, query }) => {
  const { value } = query;
  const product: Product = await fetchProduct({ handle: value as string });

  return {
    props: {
      product,
    },
  };
};

export type CourseDetailsPropsType = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function CourseDetailsPage({ product }: CourseDetailsPropsType) {
  const { customer, token } = useContext(UserContext);
  const router = useRouter();

  if (router.isFallback) {
    return <ProductPageSkeleton />;
  }

  return (
    <Container>
      <NextSeo title={`${product.title} | Mount Olive Family Medicine Center Online Training`} />
      <ProductJsonLd
        productName={product.title}
        offers={[
          {
            price: (product.defaultVariant.price.units / 100).toString(),
            priceCurrency: product.defaultVariant.price.currency,
            seller: {
              name: "Mt. Olive Family Medicine Center, Inc.",
            },
            itemCondition: "https://schema.org/NewCondition",
            availability: "https://schema.org/InStock",
          },
        ]}
        mpn={product.mpn}
      />
      <Spacer size="md" />
      <div className="product">
        <img src={product.thumbnail.src} alt={product.title} />
        <div className="product-details">
          <h1>{product.title}</h1>
          <div className="sku">SKU {product.mpn}</div>
          <div className="description" dangerouslySetInnerHTML={{ __html: product.longDescription }} />
          {product.course && product.course.hasCoursePackages ? (
            !customer?.guest ? (
              <EnrollButton token={token} course={product.course} />
            ) : (
              <LinkButton href="/login">Sign in to enroll</LinkButton>
            )
          ) : (
            <LinkButton href="/login">Sign in to enroll</LinkButton>
          )}
        </div>
      </div>
      {/* language=CSS */}
      <style jsx>
        {`
          .product {
            display: grid;
            grid-row-gap: 20px;
          }

          .product img {
            width: 100%;
            max-width: 100%;
          }

          .description {
            font-size: 14px;
            line-height: 20px;
            margin: 0 0 20px 0;
          }

          h1 {
            font-size: 24px;
            font-weight: 500;
            margin: 0 0 10px 0;
            color: rgb(0, 102, 204);
          }

          .sku {
            font-weight: bold;
            font-size: 14px;
            line-height: 20px;
            margin: 15px 0;
          }

          .error {
            color: rgb(255, 0, 0);
          }

          @media screen and (min-width: 1024px) {
            .product {
              grid-template-columns: 1fr 1fr;
              grid-gap: 40px;
            }
          }
        `}
      </style>
    </Container>
  );
}

CourseDetailsPage.Layout = MainLayout;
